import React, { useState, useRef, useCallback } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import {
  X,
  CheckCircle,
  WarningCircle,
  CircleNotch,
  CaretLeft,
  Camera,
  ImageSquare,
} from '@phosphor-icons/react'
import { useNavigate } from 'react-router-dom'
import { inviteService, GroupPreview, isExpectedInviteError } from '@/services/inviteService'
import QrScanner from 'qr-scanner'
import { formatCurrencyName } from '@/utils/abacus/finance'
import { usePremiumModalStore } from '@/stores/usePremiumModalStore'
import QRScannerSheet from './QRScannerSheet'
import { logError } from '@/lib/log'

interface JoinByCodeSheetProps {
  isOpen: boolean
  onClose: () => void
}

type SheetStage = 'input' | 'preview' | 'success'

export default function JoinByCodeSheet({ isOpen, onClose }: JoinByCodeSheetProps) {
  const navigate = useNavigate()

  // States
  const [stage, setStage] = useState<SheetStage>('input')
  const [code, setCode] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [preview, setPreview] = useState<GroupPreview | null>(null)
  const [isLimitReached, setIsLimitReached] = useState(false)
  const [isScannerOpen, setIsScannerOpen] = useState(false)
  const openModal = usePremiumModalStore((state: any) => state.openModal)
  const [isProcessingImage, setIsProcessingImage] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  // Reset state when opened/closed
  React.useEffect(() => {
    if (!isOpen) {
      setTimeout(() => {
        setStage('input')
        setCode('')
        setError(null)
        setPreview(null)
        setIsLimitReached(false)
        setLoading(false)
      }, 300)
    }
  }, [isOpen])

  const handlePreview = async () => {
    const trimmedCode = code.trim()
    if (trimmedCode.length < 4) {
      setError('Geçerli bir davet kodu giriniz (en az 4 karakter).')
      return
    }

    setError(null)
    setLoading(true)

    try {
      const details = await inviteService.getInviteAccessDetails(trimmedCode)
      if (!details.preview) {
        setError('Geçersiz veya süresi dolmuş davet kodu.')
      } else {
        setPreview(details.preview)
        setIsLimitReached(details.isLimitReached)
        setStage('preview')
      }
    } catch (err) {
      logError(err)
      setError('Bir hata oluştu, lütfen tekrar deneyin.')
    } finally {
      setLoading(false)
    }
  }

  // QR code scanned from camera
  // QRScannerSheet zaten URL'den kodu çıkarıyor.
  // Tarayıcı okuduğunda, telefonun kendi kamerasıyla aynı akışı kullanıyoruz:
  // /invite/{code} rotasına yönlendiriyoruz (kanıtlanmış çalışan yol).
  const handleQRScanned = useCallback((scannedCode: string) => {
    const trimmed = scannedCode.trim()
    if (!trimmed || trimmed.length < 4) {
      setError('Taranan kod çok kısa. Lütfen geçerli bir davetiye QR kodu tarayın.')
      return
    }

    // Tam sayfa yükleme ile invite sayfasına git
    // (telefonun kamerasıyla aynı davranış — full page load)
    window.location.href = `/invite/${trimmed}`
  }, [])

  // Gallery image upload handler
  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    setIsProcessingImage(true)
    setError(null)

    try {
      const scanResult = await QrScanner.scanImage(file, { returnDetailedScanResult: true })
      const result = scanResult.data

      // Extract invite code from URL or use raw text
      let extractedCode = result
      try {
        const url = new URL(result)
        const pathCode = url.pathname.split('/').pop()
        const paramCode = url.searchParams.get('code')
        extractedCode = paramCode || pathCode || result
      } catch {
        // Not a URL, use as-is
      }

      const trimmed = extractedCode.trim()
      setCode(trimmed)

      // Auto-trigger preview
      if (trimmed.length >= 4) {
        setLoading(true)
        const details = await inviteService.getInviteAccessDetails(trimmed)
        if (!details.preview) {
          setError('Görseldeki QR koduna ait geçerli bir davet bulunamadı.')
        } else {
          setPreview(details.preview)
          setIsLimitReached(details.isLimitReached)
          setStage('preview')
        }
        setLoading(false)
      } else {
        setError('Görseldeki kod çok kısa.')
      }
    } catch {
      setError('Görselde QR kod bulunamadı. Lütfen net bir davetiye görseli seçin.')
    } finally {
      setIsProcessingImage(false)
      // Reset file input
      if (fileInputRef.current) fileInputRef.current.value = ''
    }
  }

  const handleJoin = async () => {
    if (!code || loading) return

    if (isLimitReached) {
      openModal({ message: "Sınır olmadan sosyalleşmeye devam etmek için Premium'a geçin." })
      return
    }

    setError(null)
    setLoading(true)

    try {
      const result = (await inviteService.acceptInvite(code.trim())) as any

      if (result?.success) {
        setStage('success')
        setTimeout(() => {
          onClose()
          window.location.href = '/groups'
        }, 1500)
      } else if (result?.message === 'Already a member') {
        setError('Zaten bu grubun üyesisiniz.')
      } else {
        setError('Gruba katılırken beklenmeyen bir hata oluştu.')
      }
    } catch (err: any) {
      if (isExpectedInviteError(err)) {
        setError(err.message)
        return
      }
      logError(err)
      const msg = err.message || ''
      if (msg.includes('Group is full') || msg.toLowerCase().includes('kapasite')) {
        setError('Maalesef grup kapasitesi dolu.')
      } else if (msg.includes('expired') || msg.toLowerCase().includes('süre')) {
        setError('Bu davetin süresi dolmuş.')
      } else {
        setError('Gruba katılma işlemi başarısız oldu.')
      }
    } finally {
      setLoading(false)
    }
  }

  return (
    <>
      <AnimatePresence>
        {isOpen && (
          <>
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={onClose}
              className="fixed inset-0 bg-black/40 backdrop-blur-sm z-[60]"
            />

            {/* Sheet */}
            <motion.div
              initial={{ y: '100%' }}
              animate={{ y: 0 }}
              exit={{ y: '100%' }}
              transition={{ type: 'spring', damping: 25, stiffness: 200 }}
              className="fixed bottom-0 left-0 right-0 bg-white rounded-t-3xl shadow-[0_-10px_40px_rgba(0,0,0,0.1)] z-[61] overflow-hidden max-h-[90vh] flex flex-col font-[Inter]"
            >
              {/* Drag Handle Area */}
              <div
                className="w-full flex justify-center pt-3 pb-2 cursor-grab active:cursor-grabbing"
                onClick={onClose}
              >
                <div className="w-12 h-1.5 bg-gray-200 rounded-full" />
              </div>

              {/* Content Container with Animation */}
              <div className="px-6 pb-8 pt-2 relative min-h-[250px]">
                <AnimatePresence mode="wait">
                  {/* STAGE 1: INPUT */}
                  {stage === 'input' && (
                    <motion.div
                      key="input"
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -20 }}
                      className="space-y-6"
                    >
                      <div className="flex items-center justify-between">
                        <h2 className="text-xl font-black text-gray-900 tracking-tight">
                          Davetiye Kodu
                        </h2>
                        <button
                          onClick={onClose}
                          className="p-2 text-gray-400 hover:text-gray-600 bg-gray-50 rounded-full transition-colors"
                          aria-label="Kapat"
                        >
                          <X size={20} weight="bold" />
                        </button>
                      </div>

                      <div className="space-y-3">
                        <p className="text-sm text-gray-500 font-medium">
                          Size verilen 6 haneli veya daha uzun davetiye kodunu giriniz.
                        </p>
                        <div className="relative">
                          <input
                            type="text"
                            value={code}
                            onChange={e => setCode(e.target.value)}
                            placeholder="Örn: Xy7Zv9"
                            className="w-full h-14 bg-gray-50 border-2 border-gray-100 rounded-2xl px-4 text-center text-xl font-bold tracking-[0.2em] text-gray-900 focus:border-[#D4AF37] focus:ring-0 transition-colors placeholder:text-gray-300 placeholder:tracking-normal placeholder:font-medium"
                            // Allow submit via Enter
                            onKeyDown={e => {
                              if (e.key === 'Enter') {
                                e.preventDefault()
                                handlePreview()
                              }
                            }}
                          />
                        </div>

                        {error && (
                          <motion.div
                            initial={{ opacity: 0, y: -5 }}
                            animate={{ opacity: 1, y: 0 }}
                            className="flex items-center gap-2 text-red-500 text-sm font-medium bg-red-50 p-3 rounded-xl border border-red-100"
                          >
                            <WarningCircle size={18} weight="fill" />
                            {error}
                          </motion.div>
                        )}
                      </div>

                      <button
                        onClick={handlePreview}
                        disabled={loading || code.trim().length < 4}
                        className="w-full py-3.5 bg-gradient-to-r from-[#e7c65c] to-[#eed161] text-espresso font-black rounded-xl shadow-md shadow-[#D4AF37]/20 flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed transition-all active:scale-95 text-sm uppercase tracking-widest"
                      >
                        {loading ? <CircleNotch size={20} className="animate-spin" /> : 'DEVAM ET'}
                      </button>

                      {/* Separator */}
                      <div className="flex items-center gap-3">
                        <div className="flex-1 h-px bg-gray-100" />
                        <span className="text-xs font-bold text-gray-300 uppercase tracking-widest">
                          veya
                        </span>
                        <div className="flex-1 h-px bg-gray-100" />
                      </div>

                      {/* QR Scan & Gallery Buttons */}
                      <div className="grid grid-cols-2 gap-3">
                        <button
                          onClick={() => setIsScannerOpen(true)}
                          disabled={loading || isProcessingImage}
                          className="flex flex-col items-center gap-2.5 py-4 px-3 bg-gray-50 hover:bg-gray-100 border-2 border-gray-100 hover:border-[#D4AF37]/30 rounded-2xl transition-all active:scale-[0.97] disabled:opacity-50"
                        >
                          <div className="w-11 h-11 bg-gradient-to-br from-[#FFFDF5] to-[#FAFAFA] rounded-xl flex items-center justify-center border border-[#D4AF37]/20 shadow-sm">
                            <Camera size={22} className="text-[#D4AF37]" weight="fill" />
                          </div>
                          <span className="text-[12px] font-bold text-gray-700">
                            Kamerayla Tara
                          </span>
                        </button>

                        <button
                          onClick={() => fileInputRef.current?.click()}
                          disabled={loading || isProcessingImage}
                          className="flex flex-col items-center gap-2.5 py-4 px-3 bg-gray-50 hover:bg-gray-100 border-2 border-gray-100 hover:border-[#D4AF37]/30 rounded-2xl transition-all active:scale-[0.97] disabled:opacity-50"
                        >
                          <div className="w-11 h-11 bg-gradient-to-br from-[#FFFDF5] to-[#FAFAFA] rounded-xl flex items-center justify-center border border-[#D4AF37]/20 shadow-sm">
                            {isProcessingImage ? (
                              <CircleNotch size={22} className="text-[#D4AF37] animate-spin" />
                            ) : (
                              <ImageSquare size={22} className="text-[#D4AF37]" weight="fill" />
                            )}
                          </div>
                          <span className="text-[12px] font-bold text-gray-700">
                            {isProcessingImage ? 'İşleniyor...' : 'Galeriden Seç'}
                          </span>
                        </button>
                      </div>

                      {/* Hidden file input */}
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/*"
                        onChange={handleFileUpload}
                        className="hidden"
                        aria-hidden="true"
                      />
                    </motion.div>
                  )}

                  {/* STAGE 2: PREVIEW */}
                  {stage === 'preview' && preview && (
                    <motion.div
                      key="preview"
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -20 }}
                      className="space-y-5"
                    >
                      {/* Header */}
                      <div className="flex items-center gap-3">
                        <button
                          onClick={() => {
                            setStage('input')
                            setError(null)
                          }}
                          className="p-2 -ml-2 text-gray-400 hover:text-gray-600 rounded-full transition-colors"
                        >
                          <CaretLeft size={24} weight="bold" />
                        </button>
                        <h2 className="text-xl font-black text-gray-900 tracking-tight flex-1">
                          Gruba Katıl
                        </h2>
                      </div>

                      {/* Compact Premium Card */}
                      <div className="relative overflow-hidden rounded-[28px] border border-amber-200/50 bg-white shadow-lg shadow-[#d4af35]/5 ring-1 ring-[#d4af35]/10">
                        {/* Decorative Blurs */}
                        <div className="absolute top-0 right-0 w-28 h-28 bg-[#d4af35]/5 rounded-full blur-3xl" />
                        <div className="absolute bottom-0 left-0 w-28 h-28 bg-[#d4af35]/10 rounded-full blur-3xl" />

                        {/* Inviter Row */}
                        <div className="flex items-center gap-3 px-5 pt-5 pb-3 relative z-10">
                          <div className="w-12 h-12 shrink-0 rounded-xl bg-gradient-to-br from-[#d4af35] via-[#F5D061] to-[#C49B2A] p-[2px] shadow-md shadow-[#d4af35]/20">
                            <div className="w-full h-full rounded-[10px] overflow-hidden bg-[#FFFDF5] flex items-center justify-center">
                              {preview.inviter_avatar ? (
                                <img
                                  src={preview.inviter_avatar}
                                  alt={preview.inviter_name}
                                  referrerPolicy="no-referrer"
                                  className="w-full h-full object-cover"
                                />
                              ) : (
                                <span className="text-lg font-black text-[#d4af35]">
                                  {preview.inviter_name?.charAt(0)}
                                </span>
                              )}
                            </div>
                          </div>
                          <div className="flex flex-col min-w-0">
                            <p className="text-sm font-bold text-gray-900 truncate">
                              {preview.inviter_name}
                            </p>
                            <p className="text-xs text-gray-400 font-medium">seni davet ediyor!</p>
                          </div>
                        </div>

                        {/* Divider */}
                        <div className="mx-5 h-px bg-amber-100" />

                        {/* Hero Group Info */}
                        <div className="text-center py-5 px-5 relative z-10">
                          <h3 className="text-2xl font-black tracking-tight text-gray-900 mb-2">
                            {(preview.group_name || '').toLocaleUpperCase('tr-TR')}
                          </h3>
                          <p className="text-base font-bold text-gray-600">
                            {preview.cycle_period === 'monthly' ? 'Aylık' : 'Haftalık'} ·{' '}
                            <span className="text-xl font-black text-gray-900">
                              {Number(preview.amount).toLocaleString('tr-TR')}{' '}
                              {formatCurrencyName(preview.currency)}
                            </span>
                          </p>
                        </div>

                        {/* Divider */}
                        <div className="mx-5 h-px bg-amber-100" />

                        {/* Frosted Avatars + Motivating Text */}
                        <div className="px-5 py-4 relative z-10">
                          <div className="flex items-center gap-3">
                            <div className="flex -space-x-2.5 shrink-0">
                              {preview.masked_members &&
                                preview.masked_members.slice(0, 5).map((m, i) => (
                                  <div
                                    key={i}
                                    className="w-9 h-9 rounded-full border-2 border-white overflow-hidden shadow-sm relative"
                                  >
                                    {m.avatar_url ? (
                                      <img
                                        src={m.avatar_url}
                                        alt="Üye"
                                        referrerPolicy="no-referrer"
                                        className="w-full h-full object-cover blur-[3px]"
                                      />
                                    ) : (
                                      <div className="w-full h-full bg-gradient-to-br from-amber-100 to-amber-200 flex items-center justify-center">
                                        <span className="text-[10px] font-bold text-amber-600 blur-[2px]">
                                          {m.initials}
                                        </span>
                                      </div>
                                    )}
                                    {/* Lock overlay */}
                                    <div className="absolute inset-0 bg-black/5 backdrop-blur-[0.5px] flex items-center justify-center">
                                      <div className="w-3 h-3 rounded-full bg-white/60 flex items-center justify-center">
                                        <div className="w-1.5 h-1.5 rounded-full bg-gray-400" />
                                      </div>
                                    </div>
                                  </div>
                                ))}
                              {preview.member_count > 5 && (
                                <div className="w-9 h-9 rounded-full border-2 border-white bg-gray-100 flex items-center justify-center">
                                  <span className="text-[10px] font-bold text-gray-500">
                                    +{preview.member_count - 5}
                                  </span>
                                </div>
                              )}
                            </div>
                            <p className="text-sm font-bold text-gray-700 leading-snug">
                              {preview.member_count === 0
                                ? 'Henüz kimse katılmadı. İlk sen ol! ✨'
                                : preview.member_count === 1
                                  ? '1 kişi katıldı, sıra sende! 🔥'
                                  : `${preview.member_count} kişi zaten katıldı! 🔥`}
                            </p>
                          </div>
                        </div>
                      </div>

                      {/* Error */}
                      {error && (
                        <motion.div
                          initial={{ opacity: 0, y: -5 }}
                          animate={{ opacity: 1, y: 0 }}
                          className="flex items-center gap-2 text-red-500 text-sm font-medium bg-red-50 p-3 rounded-xl border border-red-100"
                        >
                          <WarningCircle size={18} weight="fill" className="shrink-0" />
                          {error}
                        </motion.div>
                      )}

                      {/* Gold CTA Button */}
                      {isLimitReached && (
                        <div className="text-center space-y-1 mb-2 mt-4 px-5">
                          <p className="text-xs font-bold text-amber-600">Ücretsiz grub limitini doldurdun.</p>
                          <p className="text-[11px] text-gray-500 font-medium leading-snug">Bu gruba katılmak için Premium'a geçmelisin.</p>
                        </div>
                      )}
                      <button
                        onClick={handleJoin}
                        disabled={loading}
                        className={`w-full py-3.5 font-black rounded-xl shadow-md flex items-center justify-center gap-2 disabled:opacity-50 transition-all active:scale-95 text-sm uppercase tracking-widest ${
                          isLimitReached
                            ? 'bg-gradient-to-r from-[#d4af35] to-[#F5D061] text-amber-950 shadow-[#D4AF37]/20'
                            : 'bg-gradient-to-r from-[#e7c65c] to-[#eed161] text-espresso shadow-[#D4AF37]/20 z-20 relative'
                        }`}
                      >
                        {loading ? (
                          <CircleNotch size={20} className="animate-spin" />
                        ) : (
                          isLimitReached ? '👑 PREMİUM İLE KATIL' : 'DAVETİ KABUL ET'
                        )}
                      </button>
                    </motion.div>
                  )}

                  {/* STAGE 3: SUCCESS */}
                  {stage === 'success' && (
                    <motion.div
                      key="success"
                      initial={{ opacity: 0, scale: 0.95 }}
                      animate={{ opacity: 1, scale: 1 }}
                      className="space-y-6 text-center py-6"
                    >
                      <div className="w-20 h-20 bg-green-50 text-green-500 rounded-full flex items-center justify-center mx-auto mb-4 border-4 border-green-100">
                        <CheckCircle size={48} weight="fill" />
                      </div>
                      <div>
                        <h2 className="text-2xl font-black text-gray-900 tracking-tight mb-2">
                          Başarılı!
                        </h2>
                        <p className="text-gray-500 font-medium">Gruba katılımınız sağlandı.</p>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>



      {/* QR Scanner Sheet */}
      <QRScannerSheet
        isOpen={isScannerOpen}
        onClose={() => setIsScannerOpen(false)}
        onCodeScanned={handleQRScanned}
      />
    </>
  )
}
